/**
 * Calorie Estimation Service
 *
 * Cardio:  MET × bodyweight_kg × duration_hours
 * Resistance: total_volume_kg × 0.086 + base metabolic cost
 *
 * If a user manually entered calories on a cardio set, those are used directly.
 * MET estimates are used as a fallback.
 */

// MET values — keyword matched against exercise name (lowercase)
const MET_KEYWORDS = [
  { keywords: ['sprint', 'interval run'],          met: 12.0 },
  { keywords: ['run', 'running', 'jog', 'jogging'], met: 9.0 },
  { keywords: ['jump rope', 'skipping'],            met: 10.0 },
  { keywords: ['hiit', 'circuit'],                  met: 8.5 },
  { keywords: ['stair', 'step mill', 'climber'],    met: 8.0 },
  { keywords: ['treadmill'],                        met: 7.5 },
  { keywords: ['rowing', 'row machine', 'rower'],   met: 7.0 },
  { keywords: ['incline walk'],                     met: 5.5 },
  { keywords: ['cycling', 'cycle', 'bike', 'spin'], met: 6.5 },
  { keywords: ['elliptical', 'cross trainer'],      met: 5.5 },
  { keywords: ['swim', 'swimming'],                 met: 6.0 },
  { keywords: ['walk', 'walking'],                  met: 4.5 },
];

const DEFAULT_MET = 6.0; // generic cardio fallback

/**
 * Returns MET value for a given exercise name
 */
export const getMET = (exerciseName = '') => {
  const name = exerciseName.toLowerCase();
  for (const { keywords, met } of MET_KEYWORDS) {
    if (keywords.some(k => name.includes(k))) return met;
  }
  return DEFAULT_MET;
};

/**
 * Estimate calories for a single cardio set.
 * Uses user-entered value if available, otherwise MET formula.
 */
export const cardioSetCalories = (log, exerciseName, bodyweightKg) => {
  // User already entered calories on the machine — trust it
  if (log.calories && log.calories > 0) return log.calories;

  if (!log.duration_seconds || !bodyweightKg) return 0;
  const met = getMET(exerciseName);
  return Math.round(met * bodyweightKg * (log.duration_seconds / 3600));
};

/**
 * Estimate calories for a single resistance set.
 * Formula: effective volume (kg) × 0.086, capped at 15 kcal per set.
 * Cap prevents heavy compound lifts (e.g. 150kg squat × 5) from wildly overestimating.
 */
export const resistanceSetCalories = (volumeKg) => {
  return Math.min(volumeKg * 0.086, 15);
};

/**
 * Calculate total estimated calories for a session.
 *
 * @param {Array} logs          - workout_logs rows (with exercise_type, duration_seconds, calories, weight, reps)
 * @param {Array} exerciseDetails - exercises rows (id, name, ...)
 * @param {number} userBodyweight - kg
 * @param {Function} getVolumeFn  - (weight, reps, exercise, bodyweight) → volumeKg
 * @returns {{ estimated: number, hasCardio: boolean, hasResistance: boolean }}
 */
export const calculateSessionCalories = (logs, exerciseDetails, userBodyweight, getVolumeFn) => {
  const exerciseMap = new Map(exerciseDetails.map(ex => [ex.id, ex]));

  let total = 0;
  let hasCardio = false;
  let hasResistance = false;

  for (const log of logs) {
    const exercise = exerciseMap.get(log.exercise_id);

    if (log.exercise_type === 'cardio' || log.exercise_type === 'stretch') {
      hasCardio = true;
      total += cardioSetCalories(log, exercise?.name || '', userBodyweight);
    } else {
      // resistance or isometric
      hasResistance = true;
      const vol = getVolumeFn(log.weight, log.reps, exercise, userBodyweight);
      total += resistanceSetCalories(vol);
    }
  }

  return {
    estimated: Math.round(total),
    hasCardio,
    hasResistance,
  };
};
